import {
	CheckCircleFilled,
	CloseCircleFilled,
	FormOutlined,
	LoadingOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Col, Row, Space, Spin } from 'antd';
import Text from 'antd/lib/typography/Text';
import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CustomNotification } from 'src/components/custom-notification';
import InfoTable from 'src/components/info-table';
import { tableColumnTextFilterConfig } from 'src/components/tableUtils';
import WeightChange from 'src/components/ui/weight-change';

import {
	MutationDeleteYouTubeVideo,
	MutationUpdateYouTubeVideo,
} from 'src/lib/gql/mutations';
import { QueryGetYouTubeVideos } from 'src/lib/gql/queries';
import { YouTubeVideoTable } from 'src/lib/interface';
import { ROUTES } from '../config/route';

const YouTubeVideos = () => {
	const {
		data: qryData,
		loading,
		refetch: refetchQuery,
	} = useQuery(QueryGetYouTubeVideos, {
		fetchPolicy: 'cache-and-network',
	});

	const p_YouTubeVideos: YouTubeVideoTable[] = [];

	qryData?.getYouTubeVideos?.forEach((video: any, i: number) => {
		p_YouTubeVideos.push({
			id: video.id || '',
			key: (i + 1).toString() || '',
			videoId: video.videoId || '',
			title: video.title || '',
			description: video.description || '',
			duration: video.duration || '',
			thumbnailUrl: video.thumbnailUrl || '',
			weight: video.weight || 0,
			enabled: video.enabled || false,
		});
	});

	const [UpdateYouTubeVideo] = useMutation(MutationUpdateYouTubeVideo, {
		onError(error) {
			CustomNotification({
				pageName: 'YouTube Videos',
				pagePrefix: 'YouTube Video',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const updateYouTubeVideo = useCallback(
		async (
			id: string,
			enabled: boolean | undefined,
			pos: number | null | undefined,
		) => {
			const rsp = await UpdateYouTubeVideo({
				variables: {
					id: id,
					enabled: enabled,
					weight: pos,
				},
			});

			if (rsp.data) {
				CustomNotification({
					pageName: 'YouTube Videos',
					pagePrefix: 'YouTube Video',
					notificationType: 'updated',
				});

				await refetchQuery();
			}
		},
		[UpdateYouTubeVideo, refetchQuery],
	);

	const [DeleteYouTubeVideo] = useMutation(MutationDeleteYouTubeVideo, {
		onError(error) {
			CustomNotification({
				pageName: 'YouTube Videos',
				pagePrefix: 'YouTube Video',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const deleteYouTubeVideo = useCallback(
		async (id: string) => {
			if (!confirm('Are you sure you want to delete?')) return;
			const rsp = await DeleteYouTubeVideo({
				variables: {
					id: id,
				},
			});

			if (rsp.data) {
				CustomNotification({
					pageName: 'YouTube Videos',
					pagePrefix: 'YouTube Video',
					notificationType: 'deleted',
				});

				await refetchQuery();
			}
		},
		[DeleteYouTubeVideo, refetchQuery],
	);

	// Fields
	const columns = [
		{
			title: 'ID',
			dataIndex: 'key',
			width: 50,
			align: 'center' as const,
			render: (key: number) => {
				return key;
			},
		},
		{
			title: 'Position',
			width: 80,
			sorter: (a: YouTubeVideoTable, b: YouTubeVideoTable) => a.weight - b.weight,
			render: ({ id, weight }: { id: string; weight: number }) => {
				return (
					<WeightChange
						pos={weight}
						onSubmitChange={(weight) => {
							updateYouTubeVideo(id, undefined, weight);
						}}
					/>
				);
			},
		},
		{
			title: 'Title',
			dataIndex: 'title',
			key: 'title',
			width: 200,
			...tableColumnTextFilterConfig<YouTubeVideoTable>(),
			onFilter: (value: any, record: YouTubeVideoTable) => {
				return record.title
					.toString()
					.toLowerCase()
					.includes(value.toString().toLowerCase());
			},
		},
		{
			title: 'Video ID',
			dataIndex: 'videoId',
			key: 'videoId',
			width: 150,
		},
		{
			title: 'Duration',
			dataIndex: 'duration',
			key: 'duration',
			width: 100,
		},
		{
			title: 'Edit',
			key: 'enabled',
			width: 80,
			align: 'center' as const,
			filters: [
				{
					text: 'Enabled',
					value: true,
				},
				{
					text: 'Disabled',
					value: false,
				},
			],
			filterMultiple: false,
			onFilter: (value: any, record: YouTubeVideoTable) => record.enabled === value,
			render: (props: { enabled: boolean; id: string; key: string }) => {
				const { enabled, id } = props;

				return (
					<Space>
						<Link to={`${ROUTES.EDIT_YOUTUBE_VIDEO.path}/${id}`}>
							<FormOutlined
								style={{
									color: 'black',
									width: '12px',
									height: '12px',
								}}
							/>
						</Link>
						<CheckCircleFilled
							onClick={() => {
								updateYouTubeVideo(id, !enabled, undefined);
							}}
							style={{
								color: enabled ? 'var(--valid)' : 'var(--inactive)',
								width: '12px',
								height: '12px',
							}}
						/>
						<CloseCircleFilled
							onClick={() => {
								deleteYouTubeVideo(id);
							}}
							style={{
								color: 'var(--invalid)',
								width: '12px',
								height: '12px',
							}}
						/>
					</Space>
				);
			},
		},
	];

	return (
		<Row>
			<Col span={24}>
				<Text className="sub-header">YouTube Videos</Text>
			</Col>

			<Col span={24}>
				<Row
					gutter={16}
					style={{
						margin: '16px 0',
					}}
					align={'bottom'}
				>
					<Link to={ROUTES.CREATE_YOUTUBE_VIDEO.path}>
						<Button className="btn-main">
							Create New YouTube Video
							<PlusOutlined
								style={{
									position: 'relative',
									top: '1.5px',
								}}
							/>
						</Button>
					</Link>
				</Row>

				<Spin indicator={<LoadingOutlined />} spinning={loading}>
					<InfoTable
						data={p_YouTubeVideos}
						columnData={columns}
						onChange={() => {
							//
						}}
						other={{
							scroll: {
								x: 900,
							},
						}}
					/>
				</Spin>
			</Col>
		</Row>
	);
};

export default YouTubeVideos;